import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { AppLayout } from '@/components/feature/AppLayout';
import { useAuthContext } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import ClientBacklog from './components/ClientBacklog';
import ClientBoard from './components/ClientBoard';
import ClientFinancial from './components/ClientFinancial';

export interface Task {
  id: string;
  project_id: string;
  title: string;
  description: string | null;
  type: 'feature' | 'bug';
  priority: string;
  board_status: string;
  requester_name: string | null;
  observations: string | null;
  estimate_hours: number;
  estimate_days: number;
  value: number;
  paid: boolean;
  sprint_desired: boolean;
  sprint_id: string | null;
  position: number;
  client_priority: number;
  created_by: string | null;
  created_at: string;
}

export interface Sprint {
  id: string;
  name: string;
  status: string;
  goal: string | null;
  start_date: string | null;
  end_date: string | null;
}

interface Project {
  id: string;
  name: string;
  description: string | null;
  status: string;
  start_date: string | null;
  end_date: string | null;
  scrum_active: boolean;
  client_id: string | null;
  _client_name: string;
}

type ActiveTab = 'board' | 'backlog' | 'financeiro';

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  proposta: { label: 'Proposta', color: 'bg-slate-100 text-slate-600' },
  ativo: { label: 'Ativo', color: 'bg-emerald-100 text-emerald-700' },
  pausado: { label: 'Pausado', color: 'bg-amber-100 text-amber-700' },
  concluido: { label: 'Concluído', color: 'bg-cyan-100 text-cyan-700' },
  cancelado: { label: 'Cancelado', color: 'bg-red-100 text-red-600' },
};

export default function ClientProjectPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile } = useAuthContext();

  const locationState = location.state as { tab?: ActiveTab; openTaskId?: string } | null;

  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<ActiveTab>(locationState?.tab || 'backlog');
  const [openTaskId, setOpenTaskId] = useState<string | null>(locationState?.openTaskId || null);

  const loadData = useCallback(async () => {
    if (!id || !user) return;
    setLoading(true);

    // Verificar acesso via project_members OU via client_id do perfil
    const { data: member } = await supabase
      .from('project_members')
      .select('project_id')
      .eq('project_id', id)
      .eq('user_id', user.id)
      .maybeSingle();

    if (!member) {
      // Verificar se o projeto pertence ao cliente do usuário
      const { data: userProf } = await supabase
        .from('profiles')
        .select('client_id')
        .eq('id', user.id)
        .maybeSingle();

      if (userProf?.client_id) {
        const { data: proj } = await supabase
          .from('projects')
          .select('id')
          .eq('id', id)
          .eq('client_id', userProf.client_id)
          .maybeSingle();

        if (!proj) {
          navigate('/app');
          return;
        }
      } else {
        navigate('/app');
        return;
      }
    }

    const [projRes, tasksRes, sprintsRes] = await Promise.all([
      supabase.from('projects').select('*').eq('id', id).maybeSingle(),
      supabase.from('tasks').select('*').eq('project_id', id).order('position').order('created_at', { ascending: false }),
      supabase.from('sprints').select('*').eq('project_id', id).order('created_at'),
    ]);

    if (projRes.data) {
      const { data: client } = projRes.data.client_id
        ? await supabase.from('clients').select('name').eq('id', projRes.data.client_id).maybeSingle()
        : { data: null };
      setProject({ ...projRes.data, _client_name: client?.name || '—' });
    }

    setTasks(tasksRes.data || []);
    setSprints(sprintsRes.data || []);
    setLoading(false);
  }, [id, user, navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  const activeSprint = sprints.find(s => s.status === 'active');
  const st = project ? (STATUS_CONFIG[project.status] || { label: project.status, color: 'bg-slate-100 text-slate-600' }) : null;

  const tabs: { key: ActiveTab; label: string; icon: string }[] = [
    { key: 'backlog', label: 'Backlog', icon: 'ri-list-check-2' },
    { key: 'board', label: 'Acompanhamento', icon: 'ri-kanban-view' },
    { key: 'financeiro', label: 'Financeiro', icon: 'ri-money-dollar-circle-line' },
  ];

  return (
    <AppLayout>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-3">
          <button
            onClick={() => navigate('/app')}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-base"></i>
          </button>
          {loading ? (
            <div className="h-6 w-48 bg-slate-100 rounded animate-pulse"></div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-slate-800 text-xl font-bold">{project?.name}</h1>
              {st && <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.color}`}>{st.label}</span>}
              {project?.scrum_active && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700">Scrum</span>
              )}
            </div>
          )}
        </div>
        {project && (
          <div className="flex items-center gap-4 text-xs text-slate-400 ml-11">
            <span><i className="ri-building-line mr-1"></i>{project._client_name}</span>
            {activeSprint && (
              <span className="text-violet-600 font-medium">
                <i className="ri-flashlight-line mr-1"></i>Sprint ativa: {activeSprint.name}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-lg w-fit mb-6">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === tab.key
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <i className={tab.icon}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-cyan-500 text-2xl"></i>
        </div>
      ) : (
        <>
          {activeTab === 'board' && (
            <ClientBoard
              tasks={tasks}
              sprints={sprints}
              project={project}
              userId={user!.id}
              userProfile={profile}
              onRefresh={loadData}
            />
          )}
          {activeTab === 'backlog' && (
            <ClientBacklog
              projectId={id!}
              tasks={tasks}
              sprints={sprints}
              userId={user!.id}
              userProfile={profile}
              project={project}
              onRefresh={loadData}
              openTaskId={openTaskId}
              onTaskOpened={() => setOpenTaskId(null)}
            />
          )}
          {activeTab === 'financeiro' && (
            <ClientFinancial tasks={tasks} projectId={id!} />
          )}
        </>
      )}
    </AppLayout>
  );
}